import {FC} from 'react'
import {useTranslation} from '@/translations/i18n.tsx'
import {
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    ModalHeader,
    ModalHeaderTitle,
    ModalParagraph
} from '@components/ui/modal-atoms'
import {Button} from '@components/ui/button'
import {AlertTriangleIcon, Layers01Icon} from '@components/ui/icon'
import {Assignment} from '@features/assignment/types.ts'
import {useCopyToClipboard} from '@hooks/useCopyToClipboard.ts'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'

const rowClassname =
    'flex justify-between items-center gap-4 py-2 w-full not-last:border-b not-last:border-neutral-200 [&>span:first-child]:text-xs [&>span:first-child]:font-medium [&>span:first-child]:text-neutral-500 [&>span:first-child]:uppercase [&>span:first-child]:tracking-[0.04em] [&>span:last-child]:text-sm [&>span:last-child]:text-neutral-900 [&>span:last-child]:text-end'
interface ThreeDScanLaunchModalProps {
    assignment: Assignment
    scanUrl: string
    isMobile: boolean
    onClose: () => void
}

export const ThreeDScanLaunchModal: FC<ThreeDScanLaunchModalProps> = ({assignment, scanUrl, isMobile, onClose}) => {
    const {t} = useTranslation()
    const {copyToClipboard} = useCopyToClipboard()

    const onConfirm = async () => {
        if (isMobile) {
            window.open(scanUrl, '_blank', 'noopener,noreferrer')
        } else {
            await copyToClipboard(scanUrl)
        }
        onClose()
    }

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="w-120" showCloseButton={false}>
                <ModalHeader className="grid grid-cols-[32px_1fr_32px] items-center justify-center">
                    <DialogClose data-slot="dialog-close" render={<ModalCloseButton />} />
                    <ModalHeaderTitle className="p-0">{t('assignment:three_d_scan:modal_title')}</ModalHeaderTitle>
                </ModalHeader>
                <ModalBody>
                    <div className="h-10 w-10 rounded-full bg-success-50 text-success-600 flex items-center justify-center shrink-0">
                        <Layers01Icon size={20} />
                    </div>
                    <ModalParagraph>{t('assignment:three_d_scan:modal_description')}</ModalParagraph>

                    <div className="flex flex-col rounded-2xl bg-neutral-100 py-2 px-4 w-full">
                        <div className={rowClassname}>
                            <span>{t('assignment:three_d_scan:listing_label')}</span>
                            <span>{assignment.entity.title ?? assignment.entity.external_id}</span>
                        </div>
                        <div className={rowClassname}>
                            <span>{t('assignment:three_d_scan:address_label')}</span>
                            <span>{assignment.entity.address ?? '-'}</span>
                        </div>
                        <div className={rowClassname}>
                            <span>{t('assignment:three_d_scan:host_name_label')}</span>
                            <span>{assignment.host.first_name}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-neutral-100">
                        <AlertTriangleIcon size={20} className="shrink-0 text-warning-600" />
                        <p className="text-sm font-medium text-neutral-900">{t('assignment:three_d_scan:warning')}</p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="flex justify-end w-full">
                        <Button onClick={onConfirm}>
                            {isMobile
                                ? t('assignment:three_d_scan:confirm_launch')
                                : t('assignment:three_d_scan:confirm_copy')}
                        </Button>
                    </div>
                </ModalFooter>
            </DialogContent>
        </Dialog>
    )
}
